/**
 * Wrangle-pattern AI Agent — session lifecycle + streaming wrapper.
 *
 * LLM backend priority:
 *   1. Ollama (local) — only when VITE_USE_OLLAMA=true and Ollama is reachable.
 *   2. ai-companion edge (server Gemini) — default cloud path for agent chat.
 *   3. Groq direct (browser SSE) — fallback when edge is unavailable via VITE_GROQ_API_KEY.
 *
 * Section optimization uses groq-llm edge (Gemini on server) via sectionOptimizationService.
 */

import { supabase } from '../../lib/supabase';
import { getCurrentUserId } from '../../utils/authUtils';
import {
  extractPlacementInstruction,
  parseCanvasSectionKeyFromText,
  parseMovedSectionKeyFromText,
  parsePlacementFromUserText,
  wantsDummyOrSampleFill,
  wantsPreserveAndExpandContent,
  wantsRemoveSection,
  parseRemoveSectionKeyFromText,
} from './canvas/canvasSectionUtils';
import { RESUME_LAYOUT_GUARDRAILS } from './resumeLayoutGuardrails';
import { stripStatusTags } from './utils/agentStatusTokens';

import type {
  AgentHistoryItem,
  AgentMessage,
  AgentMessageRow,
  AgentRole,
  AgentSession,
  AgentSessionRow,
} from './types';
import { rowToMessage, rowToSession } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Ollama config (local dev)
// ─────────────────────────────────────────────────────────────────────────────

const USE_OLLAMA: boolean =
  (import.meta.env.VITE_USE_OLLAMA as string | undefined) === 'true';

const OLLAMA_BASE_URL: string =
  (import.meta.env.VITE_OLLAMA_URL as string | undefined) ?? 'http://localhost:11434';

/**
 * Preferred local model. qwen2.5:latest is significantly more capable than
 * llama3.2:3b for career / reasoning tasks. Falls back to llama3.2:3b if the
 * preferred model is not pulled.
 */
const OLLAMA_MODEL_PREFERRED = 'qwen2.5:latest';
const OLLAMA_MODEL_FALLBACK   = 'llama3.2:3b';
const OLLAMA_MODEL: string =
  (import.meta.env.VITE_OLLAMA_MODEL as string | undefined) ?? OLLAMA_MODEL_PREFERRED;

/** Maximum sessions shown in the HistorySidebar. */
export const HISTORY_PAGE_SIZE = 30;

import { deriveTaskSessionTitle } from './agentMessageWindow';

/** Trim a freshly-typed user message into a session title (≤ 60 chars). */
export function deriveSessionTitle(firstUserMessage: string): string {
  return deriveTaskSessionTitle(firstUserMessage);
}

const GENERIC_SESSION_TITLES = new Set([
  'untitled chat',
  'new chat',
  'new search',
]);

export function isGenericSessionTitle(title: string | null | undefined): boolean {
  const normalized = title?.replace(/\s+/g, ' ').trim().toLowerCase();
  return !normalized || GENERIC_SESSION_TITLES.has(normalized);
}

// ─────────────────────────────────────────────────────────────────────────────
// Session CRUD
// ─────────────────────────────────────────────────────────────────────────────

export async function createSession(initialTitle: string | null = null): Promise<AgentSession> {
  const userId = await getCurrentUserId();
  if (!userId) throw new Error('Not signed in');

  const { data, error } = await supabase
    .from('ai_agent_sessions')
    .insert({ user_id: userId, title: initialTitle })
    .select('*')
    .single();

  if (error || !data) {
    throw new Error(`Could not create agent session: ${error?.message ?? 'unknown error'}`);
  }
  return rowToSession(data as AgentSessionRow);
}

export async function listRecentSessions(limit = HISTORY_PAGE_SIZE): Promise<AgentSession[]> {
  const userId = await getCurrentUserId();
  if (!userId) return [];

  const { data, error } = await supabase
    .from('ai_agent_sessions')
    .select('*')
    .eq('user_id', userId)
    .order('updated_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.warn('[ai-agent] listRecentSessions failed:', error.message);
    return [];
  }
  return ((data ?? []) as AgentSessionRow[]).map(rowToSession);
}

export async function renameSession(sessionId: string, title: string): Promise<void> {
  const trimmed = title.trim().slice(0, 200);
  await supabase
    .from('ai_agent_sessions')
    .update({ title: trimmed.length ? trimmed : null })
    .eq('id', sessionId);
}

export async function deleteSession(sessionId: string): Promise<void> {
  await supabase.from('ai_agent_sessions').delete().eq('id', sessionId);
}

// ─────────────────────────────────────────────────────────────────────────────
// Message CRUD
// ─────────────────────────────────────────────────────────────────────────────

export async function listMessages(sessionId: string): Promise<AgentMessage[]> {
  const { data, error } = await supabase
    .from('ai_agent_messages')
    .select('*')
    .eq('session_id', sessionId)
    .order('ts', { ascending: true });

  if (error) {
    console.warn('[ai-agent] listMessages failed:', error.message);
    return [];
  }
  return ((data ?? []) as AgentMessageRow[]).map(rowToMessage);
}

export async function countMessages(sessionId: string): Promise<number> {
  const { count, error } = await supabase
    .from('ai_agent_messages')
    .select('*', { count: 'exact', head: true })
    .eq('session_id', sessionId);

  if (error) {
    console.warn('[ai-agent] countMessages failed:', error.message);
    return 0;
  }
  return count ?? 0;
}

/** Most recent N messages (chronological order). */
export async function listMessagesRecent(
  sessionId: string,
  limit: number,
): Promise<AgentMessage[]> {
  const { data, error } = await supabase
    .from('ai_agent_messages')
    .select('*')
    .eq('session_id', sessionId)
    .order('ts', { ascending: false })
    .limit(limit);

  if (error) {
    console.warn('[ai-agent] listMessagesRecent failed:', error.message);
    return [];
  }
  return ((data ?? []) as AgentMessageRow[])
    .map(rowToMessage)
    .reverse();
}

/** Messages strictly before `beforeTs` (chronological). */
export async function listMessagesBefore(
  sessionId: string,
  beforeTs: string,
  limit: number,
): Promise<AgentMessage[]> {
  const { data, error } = await supabase
    .from('ai_agent_messages')
    .select('*')
    .eq('session_id', sessionId)
    .lt('ts', beforeTs)
    .order('ts', { ascending: false })
    .limit(limit);

  if (error) {
    console.warn('[ai-agent] listMessagesBefore failed:', error.message);
    return [];
  }
  return ((data ?? []) as AgentMessageRow[])
    .map(rowToMessage)
    .reverse();
}

export async function getSessionById(sessionId: string): Promise<AgentSession | null> {
  const userId = await getCurrentUserId();
  if (!userId) return null;

  const { data, error } = await supabase
    .from('ai_agent_sessions')
    .select('*')
    .eq('id', sessionId)
    .eq('user_id', userId)
    .maybeSingle();

  if (error || !data) return null;
  return rowToSession(data as AgentSessionRow);
}

export async function appendMessage(
  sessionId: string,
  role: AgentRole,
  content: string,
): Promise<AgentMessage> {
  const { data, error } = await supabase
    .from('ai_agent_messages')
    .insert({ session_id: sessionId, role, content })
    .select('*')
    .single();

  if (error || !data) {
    throw new Error(`Could not append message: ${error?.message ?? 'unknown error'}`);
  }

  if (role === 'user') {
    try {
      const { data: session } = await supabase
        .from('ai_agent_sessions')
        .select('title')
        .eq('id', sessionId)
        .single();

      if (isGenericSessionTitle(session?.title)) {
        await supabase
          .from('ai_agent_sessions')
          .update({ title: deriveSessionTitle(content) })
          .eq('id', sessionId);
      } else {
        await supabase
          .from('ai_agent_sessions')
          .update({ updated_at: new Date().toISOString() })
          .eq('id', sessionId);
      }
    } catch {
      /* non-fatal */
    }
  }

  return rowToMessage(data as AgentMessageRow);
}

export async function updateMessageContent(
  messageId: string,
  content: string,
): Promise<void> {
  await supabase
    .from('ai_agent_messages')
    .update({ content })
    .eq('id', messageId);
}

// ─────────────────────────────────────────────────────────────────────────────
// streamCompletion options / result
// ─────────────────────────────────────────────────────────────────────────────

export interface StreamCompletionOptions {
  systemPromptHint?: string | null;
  responseLanguage?: string;
  country?: string;
  /** Full user profile context injected at the TOP of the system prompt. */
  userContext?: string | null;
  /** Override the Ollama model (e.g. "llama3.2:3b", "mistral:latest"). */
  model?: string;
}

export interface StreamCompletionResult {
  content:     string;
  /** Action tag emitted by the LLM. */
  action?:     string;
  /** Legacy field — kept for job_search / web_search backward compatibility. */
  query?:      string;
  /** All other params from the action JSON when action is not job_search/web_search. */
  actionData?: Record<string, unknown>;
  /** Milliseconds taken by the LLM — shown as "Thought for X.Xs" in UI. */
  thinkingMs?: number;
  /** Which model was used, e.g. "qwen2.5:latest" or "groq/llama-3.3-70b". */
  model?:      string;
  /** Which backend served the request. */
  backend?:    'ollama' | 'groq' | 'gemini';
  /** Raw <think>…</think> block content stripped from the response, shown separately in UI. */
  thinking?:   string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Ollama: check availability
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Check if Ollama is reachable AND has CORS configured for the current origin.
 *
 * We use GET /api/tags (not POST /api/chat) for the availability check because:
 * - GET /api/tags is fast — no model loading, instant response
 * - It IS a cross-origin request (localhost:5173 → localhost:11434), so the
 *   browser WILL include an Origin header and Ollama WILL send back
 *   Access-Control-Allow-Origin if ORIGINS is configured.
 * - We read the CORS response header directly to know if chat POSTs will work.
 *
 * Using POST /api/chat for this check was wrong because large models
 * (e.g. gpt-oss:20b) can take 10–30 s to load — any short timeout
 * looked like a CORS failure even when CORS was fine.
 */
// Cache the availability result so we don't send a cold POST on every message.
// Resets after 60 s so a restart of Ollama is eventually detected.
let _ollamaAvailableCache: { ok: boolean; ts: number } | null = null;
const OLLAMA_CACHE_TTL_MS = 60_000;

async function isOllamaAvailable(): Promise<boolean> {
  const now = Date.now();
  if (_ollamaAvailableCache && now - _ollamaAvailableCache.ts < OLLAMA_CACHE_TTL_MS) {
    return _ollamaAvailableCache.ok;
  }
  // Fast path: just check GET /api/tags (no model loading, instant)
  // The full POST probe is only done by probeOllama() which is called from
  // the UI "Test connection" button and on mount — not on every message.
  try {
    const ctrl  = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 2_000);
    const res   = await fetch(`${OLLAMA_BASE_URL}/api/tags`, { signal: ctrl.signal });
    clearTimeout(timer);
    const ok = res.ok;
    _ollamaAvailableCache = { ok, ts: now };
    return ok;
  } catch {
    _ollamaAvailableCache = { ok: false, ts: now };
    return false;
  }
}

/** Call after a successful connection test to immediately mark Ollama as available. */
export function markOllamaAvailable(available: boolean): void {
  _ollamaAvailableCache = { ok: available, ts: Date.now() };
}

/**
 * Auto-detect the best available Ollama model.
 * Priority: VITE_OLLAMA_MODEL (exact) → VITE_OLLAMA_MODEL (prefix match)
 *           → llama3.2:3b → first available model.
 */
async function resolveOllamaModel(): Promise<string> {
  try {
    const res = await fetch(`${OLLAMA_BASE_URL}/api/tags`);
    if (!res.ok) return OLLAMA_MODEL_FALLBACK;
    const data = (await res.json()) as { models?: { name: string }[] };
    const names = (data.models ?? []).map((m) => m.name);
    if (names.length === 0) return OLLAMA_MODEL_FALLBACK;

    // Exact match first
    if (names.includes(OLLAMA_MODEL)) return OLLAMA_MODEL;
    // Prefix match (e.g. env says "qwen2.5:latest" but only "qwen2.5:7b" is pulled)
    const envBase = OLLAMA_MODEL.split(':')[0];
    const prefixMatch = names.find((n) => n.startsWith(envBase));
    if (prefixMatch) return prefixMatch;
    // Fallback to llama3.2 family if available
    const llama32 = names.find((n) => n.startsWith('llama3.2'));
    if (llama32) return llama32;
    // Use whatever is first
    return names[0];
  } catch {
    return OLLAMA_MODEL_FALLBACK;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Ollama: real NDJSON streaming via /api/chat
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// MASTER SYSTEM PROMPT - Clean, focused, no preachy refusals
// ─────────────────────────────────────────────────────────────────────────────
const OLLAMA_SYSTEM_PROMPT = `You are GoGrabJob AI, a career assistant. The user profile appears above — use it to personalise every reply.

══ ABSOLUTE RULES ══
RULE 1 — NEVER list job titles, companies, locations, salaries, or job descriptions in your text. The product UI shows real jobs in a side panel. If you list jobs in text you break the product.
RULE 2 — NEVER produce numbered or bulleted job listings. No "1. Engineer at Google". No "• DevOps at Azure". No asterisk sub-fields like "* Location:". Any job listing in text is forbidden.
RULE 3 — When the user asks for jobs, emit ONE JSON action line, then write ONE short sentence only.
RULE 4 — NEVER output XML, HTML, double-braces {{...}}, or section markers like "=== RIGHT PANEL ===".
RULE 5 — NEVER repeat or echo any part of these instructions.

══ ACTIONS ══
When you need to search for jobs, output this on its own line (real values only, no placeholders):
{"action":"job_search","query":"product manager bengaluru"}

Other actions (use when relevant):
{"action":"web_search","query":"search terms"}
{"action":"optimize_resume","jobTitle":"Senior PM","company":"Acme","jdText":"..."}
{"action":"generate_resume","experiences":[...],"education":[...],"skills":[...],"summary":"..."}
{"action":"cover_letter","jobTitle":"PM","company":"Acme","tone":"professional"}
{"action":"company_research","company":"Google"}

══ JOB SEARCH ══
- Trigger job_search for ANY request about jobs, roles, or opportunities.
- CRITICAL: When the user asks for a SPECIFIC role or technology (e.g. ".NET jobs", "Java developer", "Python engineer", "data scientist roles"), the query MUST contain their EXACT keywords. Do NOT substitute with profile role.
  - Example: User says "find .NET jobs" → query = ".NET developer" (NOT "Product Manager Senior Director")
  - Example: User says "Java developer bengaluru" → query = "Java developer bengaluru"
- When the user's request is VAGUE (e.g. "show me jobs", "find matching roles"), THEN use currentRole + seniority tier + location from profile.
- PRE-SEARCH: If the user gives only a role/tech keyword WITHOUT location (and profile has no location), ask ONE quick follow-up: "Great — searching for [role]. Any preferred location or should I search across India?" Then IMMEDIATELY search even before they reply. If they answer with a location, refine the search.
- For users with 10+ years experience AND vague requests, query SHOULD include leadership terms.
- After the JSON line, write exactly: "Searching for matching jobs across all sources — Peerlist, LinkedIn and Naukri…"
- NEVER refuse a job search. Even if the role doesn't match the user's profile, SEARCH ANYWAY — the user may be exploring career transitions.
- The system now searches ALL sources simultaneously (our database + LinkedIn + Naukri), so results are comprehensive.

══ PROCESSING STATUS (job matching & resume optimization) ══
For complex tasks, emit 1–3 live status lines BEFORE your final answer (each on its own line):
[STATUS: Analyzing profile context...]
[STATUS: Evaluating seniority match...]
[STATUS: Cross-referencing role requirements...]
These are UI-only metadata — do NOT repeat them in the final sentence.

══ JOB MATCHING GUARDRAILS ══
- Use TOTAL career years from the profile — never assume the user is junior if they have 8+ years.
- For profile-based (vague) searches: Prioritize leadership / strategic roles for experienced users.
- EXCEPTION: If the user EXPLICITLY asks for a different role/domain (e.g. "find me Python jobs" when they're a PM), RESPECT their intent. They may be exploring career transitions or have side interests. Never refuse.
- The user's engagement is more valuable than strict filtering. Show them what they ask for.

══ ENGAGEMENT — MANDATORY ══
After EVERY completed action, you MUST end with a short next-step suggestion. Follow this engagement ladder:

AFTER JOB SEARCH:
- "Want me to **optimize your resume** for any of these roles? Just click on a job."
- "I can also **write a cover letter** tailored to a specific position."
- "Would you like to **search with different keywords** or in a specific city?"

AFTER RESUME OPTIMIZATION:
- "Your resume is now optimized. Want me to **write a matching cover letter**?"
- "Should I **search for similar roles** in other companies?"
- "Here's a quick tip: [share a relevant interview/career insight]"

AFTER RESUME BUILD:
- "Your resume is ready! Want me to **save it to your profile** for future use?"
- "Should I **optimize it for a specific job listing**? Paste the JD and I'll tailor it."
- "I can also **search for jobs** matching your new resume."

AFTER COVER LETTER:
- "Your cover letter is ready. Want to **download it as PDF**?"
- "Should I **prepare interview talking points** for this role?"
- "Want me to **find more similar openings** at other companies?"

GENERAL: Never end a turn with a bare statement. Always invite the user to continue with 2-3 specific, contextual suggestions.

══ PROFILE NOT CONFIGURED ══
If the user's profile is thin or empty:
- Do NOT lecture them about completing their profile before every action.
- Do NOT refuse to search for jobs or help with resume tasks.
- If they want to search jobs, SEARCH IMMEDIATELY with their keywords.
- If they want to build a resume, start the conversational interview right away.
- Gently mention profile completion as a SUGGESTION (not a blocker) at the end: "Tip: Completing your profile will unlock AI-matched job recommendations."
- The goal is ENGAGEMENT first — a user who gets value will complete their profile naturally.

══ CANVAS CONTEXT ══
When the resume canvas is active, you KNOW which sections exist (listed in context). Structural edits (move/reorder sections, fill education, add projects, dummy values, **remove/delete sections**) are executed by a separate canvas engine — NOT by you in chat.
${RESUME_LAYOUT_GUARDRAILS}
NEVER say a section was "updated", "moved", "removed", or "filled" in your reply unless you are only acknowledging that the user asked (the canvas will change on the right). Do NOT invent degrees, employers, or project text in chat.
If the user asks to **remove** or **delete** a section, reply in ONE short sentence that the canvas engine will remove it — do NOT rewrite Work Experience or merge content elsewhere.
If the user asks to move, add, or edit a resume section, reply in ONE short sentence: you are applying that to the canvas — do not paste rewritten section content in chat.
For general questions (e.g. "what sections should an ATS resume have?"), answer briefly and ask which section to improve next.
When CANVAS JOB LOCK appears in context: NEVER emit job_search or web_search. NEVER list other companies or roles. "yes"/"ok" after a re-optimization question means continue THAT job only — not a new job search.

══ CONVERSATION AWARENESS ══
- ALWAYS read the full conversation history before responding. Do NOT repeat suggestions you already made.
- If the user's last message is a casual greeting ("hi", "hello", "hey"), respond warmly and ask what they need help with. Do NOT immediately trigger actions.
- If the user asks a question (e.g. "what should I do?", "how can I improve?"), ANSWER the question conversationally FIRST, then suggest actions at the end.
- Do NOT trigger job_search for general career advice questions. Only trigger job_search when the user EXPLICITLY mentions wanting to find/see/search for jobs.
- Track what you've already done in this conversation — if you already searched jobs, don't search again unless asked. If you already optimized a resume, don't re-optimize unless asked.
- Be concise. Maximum 3-4 sentences per response unless the user asks for detailed advice.
- NEVER start responses with "I'd be happy to help!" or similar filler phrases. Get straight to the point.

══ PROFILE NOTE ══
"Experience" = TOTAL career years, not years in current role only.`;



// ─────────────────────────────────────────────────────────────────────────────
// Action-tag helpers (A-2)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Find the opening brace of the first JSON object that contains an "action" key.
 * Handles both `{"action"` and `{ "action"` (LLMs often add a space after `{`).
 * Returns the parsed object or null if not found / invalid JSON.
 */
function extractFirstJsonObject(text: string): Record<string, unknown> | null {
  // Find the "action" key anywhere in the text
  const actionKeyIdx = text.indexOf('"action"');
  if (actionKeyIdx === -1) return null;

  // Walk backwards from "action" to find the enclosing `{`
  let braceStart = -1;
  for (let i = actionKeyIdx - 1; i >= 0; i--) {
    const ch = text[i];
    if (ch === '{') { braceStart = i; break; }
    // Allow only whitespace between `{` and `"action"`
    if (ch !== ' ' && ch !== '\t' && ch !== '\n' && ch !== '\r') break;
  }
  if (braceStart === -1) return null;

  // Walk forward from `{` matching balanced braces
  let depth = 0;
  for (let i = braceStart; i < text.length; i++) {
    if (text[i] === '{') depth++;
    else if (text[i] === '}') {
      depth--;
      if (depth === 0) {
        try {
          const parsed = JSON.parse(text.slice(braceStart, i + 1)) as Record<string, unknown>;
          if (typeof parsed.action === 'string') return parsed;
        } catch {
          return null;
        }
      }
    }
  }
  return null;
}

/**
 * Parse any action tag from LLM output.
 * - `job_search` / `web_search` → return `{ action, query }` (legacy shape)
 * - All other actions          → return `{ action, actionData: <full parsed object> }`
 * Returns null if no action tag found.
 */
function parseActionTag(text: string): Pick<StreamCompletionResult, 'action' | 'query' | 'actionData'> | null {
  const parsed = extractFirstJsonObject(text);
  if (!parsed || typeof parsed.action !== 'string') return null;
  const action = parsed.action;
  console.log('[agentSession] action parsed', { action, actionData: parsed });
  if (action === 'job_search' || action === 'web_search') {
    return { action, query: typeof parsed.query === 'string' ? parsed.query : undefined };
  }
  return { action, actionData: parsed };
}

/** Strip well-formed action-tag JSON from streaming content. */
const ACTION_TAG_RE = /\n?\{\s*"action"\s*:\s*"[^"]+"[^}]*\}/g;

/** Strip double-brace variants emitted by some small models: {{action:...}} */
const ACTION_DBL_BRACE_RE = /\{\{[^}]*"?action"?\s*:\s*"?[^",}]+"?[^}]*\}\}/g;

/** Strip action label prefixes that small models echo (JOB_SEARCH:, === WEB_SEARCH ===, etc.) */
const ACTION_LABEL_RE = /(?:^|\n)\s*(?:===\s*)?(?:JOB_SEARCH|WEB_SEARCH|OPTIMIZE|COVER_LETTER|COMPANY_RESEARCH|MATCH_EXPLAIN|NAVIGATE|JOB SEARCH RESULT|JOBSEARCH|RIGHT PANEL)\s*:?\s*(?:===)?\s*/gi;

/**
 * Strip XML job-listing blocks that small models fabricate.
 * Covers: <results>…</results>, <result>…</result>, <title>…</title>,
 *         <description>…</description>, <link …>…</link>, <company>…</company>
 * and any === SECTION HEADER === delimiters.
 */
const XML_JOB_BLOCK_RE = /<\/?(results?|title|description|link|company|experience|salary|location|type|job)[^>]*>([^<]*)?/gi;
const SECTION_HDR_RE   = /={2,}[^=\n]+=+/g;

/**
 * Fake job listings hallucinated by small models — covers several output styles:
 *
 * Numbered:    "1. Product Manager at Google"
 * Bullet:      "• Cloud Engineer at Azure, an AWS-owned..."
 * Bold:        "**Senior Engineer** at Microsoft"
 * Sub-fields:  "  * Location: San Francisco, CA"
 *              "  * Job Type: Full-time"
 *              "  * Job Description: ..."
 * Closing:     "These are just a few examples of job openings..."
 *              "I can try to provide more suggestions..."
 *              "By the way, would you like me to..."
 */
const FAKE_JOB_BULLET_RE = /^[ \t]*(?:[•\-\*]|\d+\.)\s+\*{0,2}[A-Z][^\n]{5,}(?:at|@|,)\s+[A-Z][^\n]{3,}\n?/gm;
const FAKE_JOB_FIELD_RE  = /^[ \t]*\*+\s*(?:Location|Job Type|Job Description|Requirements?|Salary|Experience)\s*:/gim;
const FAKE_JOB_CLOSE_RE  = /^(?:These are just a few|I can try to provide|By the way|Here are a few|Here is a (?:list|selection)|Feel free to ask|Would you like me|Let me know if)[^\n]*\n?/gim;

function stripActionTag(text: string): string {
  const stripped = text
    .replace(ACTION_TAG_RE, '')
    .replace(ACTION_DBL_BRACE_RE, '')
    .replace(SECTION_HDR_RE, '')
    .replace(ACTION_LABEL_RE, '\n')
    .replace(XML_JOB_BLOCK_RE, '')
    .replace(FAKE_JOB_BULLET_RE, '')
    .replace(FAKE_JOB_FIELD_RE, '')
    .replace(FAKE_JOB_CLOSE_RE, '')
    .replace(/^[{}…\.]{1,3}\s*$/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trimEnd();
  return stripStatusTags(stripped);
}

/** Extract and strip <think>…</think> and <redacted_thinking>…</redacted_thinking> blocks. */
function extractThinkBlock(text: string): { thinking: string; clean: string } {
  const thinkRe = /<(?:think|redacted_thinking)>([\s\S]*?)<\/(?:think|redacted_thinking)>/gi;
  let thinking = '';
  const clean = text.replace(thinkRe, (_, inner: string) => {
    thinking += (thinking ? '\n' : '') + inner.trim();
    return '';
  }).trim();
  return { thinking, clean };
}

async function streamCompletionOllama(
  prompt: string,
  history: AgentHistoryItem[],
  onDelta: (delta: string, accumulated: string) => void,
  signal?: AbortSignal,
  opts: StreamCompletionOptions = {},
): Promise<StreamCompletionResult> {
  if (signal?.aborted) throw new DOMException('aborted', 'AbortError');

  // Resolve model: use the explicit selection, or auto-detect (ignore groq-cloud: ids)
  let requestedModel = opts.model?.trim() || '';
  if (
    isCloudGeminiModel(requestedModel) ||
    requestedModel.startsWith(WEB_MODEL_PREFIX)
  ) {
    requestedModel = '';
  }
  const model = requestedModel || await resolveOllamaModel();

  console.info('[ai-agent] using Ollama at', OLLAMA_BASE_URL, 'model:', model);

  const systemContent = [
    // User profile block goes FIRST so the model always knows who it's talking to
    opts.userContext?.trim() ?? '',
    OLLAMA_SYSTEM_PROMPT,
    opts.systemPromptHint?.trim() ?? '',
    opts.country ? `User is based in: ${opts.country}.` : '',
  ].filter(Boolean).join('\n\n');

  const messages: Array<{ role: string; content: string }> = [
    { role: 'system', content: systemContent },
    ...history.slice(-10).map((h) => ({ role: h.role, content: h.content })),
    { role: 'user', content: prompt },
  ];

  const t0 = Date.now();

  // ── Log prompt ─────────────────────────────────────────────────────
  console.group(`%c[Ollama] ➤ ${model}`, 'color:#6C47FF;font-weight:bold');
  console.log('%cPrompt', 'color:#888;font-size:11px', prompt);
  if (opts.userContext) {
    console.log('%cUser Context', 'color:#10b981;font-size:11px', opts.userContext);
  }
  if (history.length > 0) {
    console.log('%cHistory (%d turns)', 'color:#888;font-size:11px', history.length, history);
  }
  console.groupEnd();

  const res = await fetch(`${OLLAMA_BASE_URL}/api/chat`, {
    method:  'POST',
    signal,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ model, messages, stream: true }),
  });

  if (!res.ok || !res.body) {
    throw new Error(`Ollama returned ${res.status}`);
  }

  const reader  = res.body.getReader();
  const decoder = new TextDecoder();
  let accumulated = '';
  let buffer      = '';

  while (true) {
    if (signal?.aborted) throw new DOMException('aborted', 'AbortError');

    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      try {
        const parsed = JSON.parse(trimmed) as {
          message?: { content?: string };
          done?: boolean;
        };
        const chunk = parsed.message?.content ?? '';
        if (chunk) {
          accumulated += chunk;
          // Strip the action-tag JSON from what we show in the chat bubble
          onDelta(chunk, stripActionTag(accumulated));
        }
      } catch { /* non-JSON line — ignore */ }
    }
  }

  const thinkingMs = Date.now() - t0;

  // ── Log response ────────────────────────────────────────────────────
  console.group(`%c[Ollama] ◀ ${model}  (${(thinkingMs/1000).toFixed(1)}s)`, 'color:#10b981;font-weight:bold');
  console.log('%cResponse', 'color:#888;font-size:11px', accumulated.trim());
  console.groupEnd();

  // Extract <think> blocks first
  const { thinking, clean: withoutThink } = extractThinkBlock(accumulated);

  // Parse action tags — all supported action types (A-2)
  const parsedAction = parseActionTag(withoutThink);
  if (parsedAction) {
    const cleanContent = stripActionTag(withoutThink).trim();
    return { content: cleanContent, ...parsedAction, thinkingMs, model, backend: 'ollama', thinking: thinking || undefined };
  }

  return { content: withoutThink.trim(), thinkingMs, model, backend: 'ollama', thinking: thinking || undefined };
}

// ─────────────────────────────────────────────────────────────────────────────
// Gemini edge path (production / Ollama unavailable) + Groq direct fallback
// ─────────────────────────────────────────────────────────────────────────────

const TYPEWRITER_TICK_MS = 18;
const MAX_AGENT_CONTEXT_CHARS = 12_000;
const AI_COMPANION_MAX_RETRIES = 2;

function capAgentContext(text: string, max = MAX_AGENT_CONTEXT_CHARS): string {
  const trimmed = text.trim();
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, max)}\n…[context truncated]`;
}

function isRetryableEdgeError(message: string): boolean {
  const m = message.toLowerCase();
  return (
    m.includes('failed to send') ||
    m.includes('network') ||
    m.includes('fetch') ||
    m.includes('timeout') ||
    m.includes('econnreset') ||
    m.includes('connection') ||
    m.includes('temporarily unavailable')
  );
}

function sleepMs(ms: number, signal?: AbortSignal): Promise<void> {
  if (signal?.aborted) return Promise.reject(new DOMException('aborted', 'AbortError'));
  return new Promise((resolve, reject) => {
    const t = window.setTimeout(resolve, ms);
    if (signal) {
      signal.addEventListener('abort', () => {
        window.clearTimeout(t);
        reject(new DOMException('aborted', 'AbortError'));
      }, { once: true });
    }
  });
}

type AiCompanionPayload = {
  message: string;
  history: AgentHistoryItem[];
  country?: string;
  user_context?: string;
  response_language?: string;
  assistant_gender?: string;
  json_only?: boolean;
  system_override?: string;
  max_tokens?: number;
  temperature?: number;
};

type AiCompanionResponse = {
  content?: string;
  action?: string;
  query?: string;
  actionData?: Record<string, unknown>;
  error?: string;
};

async function invokeAiCompanion(
  body: AiCompanionPayload,
  signal?: AbortSignal,
): Promise<AiCompanionResponse> {
  let lastMessage = 'AI request failed';

  for (let attempt = 0; attempt <= AI_COMPANION_MAX_RETRIES; attempt++) {
    if (signal?.aborted) throw new DOMException('aborted', 'AbortError');

    try {
      const { data, error } = await supabase.functions.invoke<AiCompanionResponse>('ai-companion', {
        body,
      });

      if (error) {
        lastMessage = error.message || lastMessage;
        if (attempt < AI_COMPANION_MAX_RETRIES && isRetryableEdgeError(lastMessage)) {
          console.warn(`[ai-companion] retry ${attempt + 1}/${AI_COMPANION_MAX_RETRIES}:`, lastMessage);
          await sleepMs(450 * (attempt + 1), signal);
          continue;
        }
        throw new Error(lastMessage);
      }

      if (data?.error && !data?.content?.trim()) {
        throw new Error(data.error);
      }

      return data ?? {};
    } catch (err) {
      if ((err as Error).name === 'AbortError') throw err;
      lastMessage = (err as Error).message || lastMessage;
      if (attempt < AI_COMPANION_MAX_RETRIES && isRetryableEdgeError(lastMessage)) {
        console.warn(`[ai-companion] retry ${attempt + 1}/${AI_COMPANION_MAX_RETRIES}:`, lastMessage);
        await sleepMs(450 * (attempt + 1), signal);
        continue;
      }
      throw err;
    }
  }

  throw new Error(lastMessage);
}

/** Non-streaming JSON/classify calls via ai-companion (server Gemini). */
async function invokeAiCompanionJson(
  systemPrompt: string,
  userMessage: string,
  opts: { maxTokens?: number; temperature?: number } = {},
  signal?: AbortSignal,
): Promise<string | null> {
  try {
    const data = await invokeAiCompanion(
      {
        message: userMessage,
        history: [],
        json_only: true,
        system_override: systemPrompt,
        max_tokens: opts.maxTokens ?? 500,
        temperature: opts.temperature ?? 0,
      },
      signal,
    );
    return (data.content ?? '').trim() || null;
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err;
    console.warn('[ai-companion] classify error:', (err as Error).message);
    return null;
  }
}

async function streamCompletionGeminiEdge(
  prompt: string,
  history: AgentHistoryItem[],
  onDelta: (delta: string, accumulated: string) => void,
  signal?: AbortSignal,
  opts: StreamCompletionOptions = {},
): Promise<StreamCompletionResult> {
  if (signal?.aborted) throw new DOMException('aborted', 'AbortError');

  const geminiModel = resolveAgentGeminiModel(opts.model);
  const systemHint = capAgentContext(
    [
      opts.userContext?.trim() ?? '',
      opts.systemPromptHint?.trim() ?? '',
    ].filter(Boolean).join('\n\n'),
  );

  console.info('[ai-agent] using Gemini edge (ai-companion):', geminiModel);

  const t0 = Date.now();
  const data = await invokeAiCompanion(
    {
      message: prompt,
      history: history.slice(-10),
      country: opts.country,
      user_context: systemHint,
      response_language: opts.responseLanguage ?? 'en',
      assistant_gender: 'female',
    },
    signal,
  );

  const fullText = (data?.content ?? '').trim();
  const thinkingMs = Date.now() - t0;

  console.group('%c[Gemini Edge] ◀  (cloud)', 'color:#f59e0b;font-weight:bold');
  console.log('%cPrompt', 'color:#888;font-size:11px', prompt);
  console.log('%cResponse (%dms)', 'color:#888;font-size:11px', thinkingMs, fullText);
  console.groupEnd();

  if (!fullText) {
    return {
      content: '',
      action: data?.action,
      query: data?.query,
      actionData: data?.actionData,
      thinkingMs,
      model: `gemini/${geminiModel}`,
      backend: 'gemini',
    };
  }

  const tokens = fullText.split(/(\s+)/);
  let accumulated = '';
  for (const token of tokens) {
    if (signal?.aborted) throw new DOMException('aborted', 'AbortError');
    accumulated += token;
    onDelta(token, stripActionTag(accumulated));
    if (token.trim().length > 0) {
      await sleepMs(TYPEWRITER_TICK_MS, signal);
    }
  }

  const { thinking, clean } = extractThinkBlock(fullText);

  const parsedFromText = parseActionTag(fullText);

  return {
    content: clean.trim() || fullText,
    action: data?.action ?? parsedFromText?.action,
    query: data?.query ?? parsedFromText?.query,
    actionData: (data?.actionData as Record<string, unknown> | undefined) ?? parsedFromText?.actionData,
    thinkingMs,
    model: `gemini/${geminiModel}`,
    backend: 'gemini',
    thinking: thinking || undefined,
  };
}

async function streamCompletionGroq(
  prompt: string,
  history: AgentHistoryItem[],
  onDelta: (delta: string, accumulated: string) => void,
  signal?: AbortSignal,
  opts: StreamCompletionOptions & { groqModel?: string } = {},
): Promise<StreamCompletionResult> {
  const groqModel = opts.groqModel ?? resolveAgentGroqModel(opts.model);
  console.info('[ai-agent] using Groq direct (fallback):', groqModel);
  return streamCompletionGroqDirect(prompt, history, onDelta, signal, { ...opts, groqModel });
}

// ─────────────────────────────────────────────────────────────────────────────
// Groq Direct (browser-side SSE) — primary cloud path for agent chat
// ─────────────────────────────────────────────────────────────────────────────

const GROQ_API_KEY: string =
  (import.meta.env.VITE_GROQ_API_KEY as string | undefined) ?? '';

/** High-quality model — web search + admin override. Replaces deprecated llama-3.3-70b. */
const GROQ_MODEL_70B = 'openai/gpt-oss-120b';
/** Fast model — agent chat default. Replaces deprecated llama-3.1-8b-instant (Aug 2026). */
const GROQ_MODEL_8B  = 'openai/gpt-oss-20b';

/** Groq decommissioned Llama 3.x IDs on 2026-08-16 — map stored selections + env vars. */
const GROQ_LEGACY_MODEL_ALIASES: Record<string, string> = {
  'llama-3.1-8b-instant':           GROQ_MODEL_8B,
  'meta-llama-3.1-8b-instant':      GROQ_MODEL_8B,
  'groq-llama-3.1-8b-instant':      GROQ_MODEL_8B,
  'llama-3.3-70b-versatile':        GROQ_MODEL_70B,
  'groq-llama-3.3-70b-versatile':   GROQ_MODEL_70B,
};

export function normalizeGroqModelId(model: string): string {
  const m = model.trim();
  return GROQ_LEGACY_MODEL_ALIASES[m] ?? m;
}

/** Sentinel prefix for Gemini cloud model strings (not Ollama). */
export const GEMINI_MODEL_PREFIX = 'gemini-cloud:';
/** Legacy alias — groq-cloud: ids still route to Gemini (primary). */
export const CLOUD_MODEL_PREFIX = 'groq-cloud:';
/** Sentinel prefix for web-augmented model strings. */
export const WEB_MODEL_PREFIX   = 'groq-web:';

export const DEFAULT_GEMINI_MODEL =
  (import.meta.env.VITE_GEMINI_AGENT_MODEL as string | undefined)?.trim() || 'gemini-3.6-flash';

/** Resume section rewrites — always Gemini on the optimize-resume edge. */
export const RESUME_OPTIMIZE_MODEL = DEFAULT_GEMINI_MODEL;

export function isCloudGeminiModel(modelId: string): boolean {
  const m = modelId.trim();
  return m.startsWith(GEMINI_MODEL_PREFIX) || m.startsWith(CLOUD_MODEL_PREFIX);
}

/** Resolve Gemini model id from env / explicit override (strips cloud prefixes). */
export function resolveAgentGeminiModel(explicit?: string): string {
  const raw = (
    explicit?.trim() ||
    (import.meta.env.VITE_GEMINI_AGENT_MODEL as string | undefined)?.trim() ||
    DEFAULT_GEMINI_MODEL
  );
  if (raw.startsWith(GEMINI_MODEL_PREFIX)) return raw.slice(GEMINI_MODEL_PREFIX.length);
  if (raw.startsWith(CLOUD_MODEL_PREFIX)) return DEFAULT_GEMINI_MODEL;
  if (raw.startsWith(WEB_MODEL_PREFIX)) {
    const inner = raw.slice(WEB_MODEL_PREFIX.length);
    return inner && !/gpt-oss|llama-3/i.test(inner) ? inner : DEFAULT_GEMINI_MODEL;
  }
  if (/gpt-oss|llama-3/i.test(raw)) return DEFAULT_GEMINI_MODEL;
  return raw;
}

/** Resolve Groq model id — used only for Groq fallback path. */
export function resolveAgentGroqModel(explicit?: string): string {
  const raw = (
    explicit?.trim() ||
    (import.meta.env.VITE_AGENT_CLOUD_MODEL as string | undefined)?.trim() ||
    GROQ_MODEL_8B
  );
  let stripped = raw;
  if (raw.startsWith(GEMINI_MODEL_PREFIX)) stripped = GROQ_MODEL_8B;
  else if (raw.startsWith(CLOUD_MODEL_PREFIX)) stripped = raw.slice(CLOUD_MODEL_PREFIX.length);
  else if (raw.startsWith(WEB_MODEL_PREFIX)) stripped = raw.slice(WEB_MODEL_PREFIX.length);
  return normalizeGroqModelId(stripped);
}

/** Re-map deprecated groq-cloud:/groq-web: ids saved in session or admin picker. */
export function migrateCloudModelSelection(modelId: string): string {
  const m = modelId.trim();
  if (!m) return DEFAULT_AGENT_CLOUD_MODEL;
  const isWeb = m.startsWith(WEB_MODEL_PREFIX);
  if (/gpt-oss|llama-3\.|groq-cloud:/i.test(m)) {
    return isWeb
      ? `${WEB_MODEL_PREFIX}${DEFAULT_GEMINI_MODEL}`
      : `${GEMINI_MODEL_PREFIX}${DEFAULT_GEMINI_MODEL}`;
  }
  if (isWeb) return `${WEB_MODEL_PREFIX}${resolveAgentGeminiModel(m)}`;
  if (isCloudGeminiModel(m) || m.startsWith(GEMINI_MODEL_PREFIX)) {
    return `${GEMINI_MODEL_PREFIX}${resolveAgentGeminiModel(m)}`;
  }
  return m;
}

/** Default cloud model for agent chat — Gemini Flash on ai-companion edge. */
export const DEFAULT_AGENT_CLOUD_MODEL = `${GEMINI_MODEL_PREFIX}${resolveAgentGeminiModel()}`;

/** Short label for admin model picker toolbar. */
export function formatAdminCloudModelShort(modelId: string): string {
  const gemini = resolveAgentGeminiModel(modelId);
  if (/flash-lite|lite/i.test(gemini)) return 'Gemini Lite';
  if (/flash/i.test(gemini)) return 'Gemini Flash';
  if (/pro/i.test(gemini)) return 'Gemini Pro';
  const tail = gemini.includes('/') ? gemini.split('/').pop()! : gemini;
  return tail.length > 20 ? `${tail.slice(0, 18)}…` : tail;
}

function isGroqRateLimited(status: number, errText?: string): boolean {
  if (status === 429) return true;
  const t = (errText ?? '').toLowerCase();
  return t.includes('rate limit') || t.includes('rate_limit');
}

function isGroq70bModel(model: string): boolean {
  const m = normalizeGroqModelId(model);
  return /120b|70b|3\.3-versatile|qwen3\.6/i.test(m) && !/20b|8b|instant/i.test(m);
}

function isGroqModelNotFound(status: number, errText?: string): boolean {
  if (status === 404) return true;
  const t = (errText ?? '').toLowerCase();
  return t.includes('model_not_found') || t.includes('does not exist');
}

function estimateTokens(text: string): number {
  return Math.ceil(text.length / 4);
}

function buildAgentChatMessages(
  prompt: string,
  history: AgentHistoryItem[],
  opts: StreamCompletionOptions,
): Array<{ role: string; content: string }> {
  const systemContent = [
    opts.userContext?.trim() ?? '',
    OLLAMA_SYSTEM_PROMPT,
    opts.systemPromptHint?.trim() ?? '',
    opts.country ? `User is based in: ${opts.country}.` : '',
  ].filter(Boolean).join('\n\n');

  const MAX_HIST_MSGS = 6;
  const MAX_MSG_CHARS = 1_200;
  const trimmedHistory = history
    .slice(-MAX_HIST_MSGS)
    .map((h) => ({
      role: h.role,
      content:
        h.content.length > MAX_MSG_CHARS
          ? h.content.slice(0, MAX_MSG_CHARS) + '\n…[truncated for context]'
          : h.content,
    }));

  return [
    { role: 'system', content: systemContent },
    ...trimmedHistory,
    { role: 'user', content: prompt },
  ];
}

async function parseOpenAiSseStream(
  body: ReadableStream<Uint8Array>,
  onDelta: (delta: string, accumulated: string) => void,
  signal?: AbortSignal,
): Promise<string> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let accumulated = '';
  let buffer = '';

  while (true) {
    if (signal?.aborted) throw new DOMException('aborted', 'AbortError');
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';
    for (const line of lines) {
      const trimmed = line.replace(/^data:\s*/, '').trim();
      if (!trimmed || trimmed === '[DONE]') continue;
      try {
        const parsed = JSON.parse(trimmed) as {
          choices?: Array<{ delta?: { content?: string } }>;
        };
        const chunk = parsed.choices?.[0]?.delta?.content ?? '';
        if (chunk) {
          accumulated += chunk;
          onDelta(chunk, stripActionTag(accumulated));
        }
      } catch { /* non-JSON SSE line */ }
    }
  }
  return accumulated;
}

async function streamCompletionCloudFallback(
  prompt: string,
  history: AgentHistoryItem[],
  onDelta: (delta: string, accumulated: string) => void,
  signal?: AbortSignal,
  opts: StreamCompletionOptions = {},
  primaryErr?: unknown,
): Promise<StreamCompletionResult> {
  const reason = (primaryErr as Error)?.message?.slice(0, 160) ?? 'unknown';
  console.warn('[ai-agent] Gemini edge unavailable — trying Groq direct:', reason);

  if (!GROQ_API_KEY) {
    throw new Error('AI unavailable — Gemini edge failed and VITE_GROQ_API_KEY is not set.');
  }

  try {
    console.info('[ai-agent] retrying with Groq', GROQ_MODEL_8B);
    return await streamCompletionGroqDirect(prompt, history, onDelta, signal, {
      ...opts,
      groqModel: GROQ_MODEL_8B,
    });
  } catch (groqErr) {
    if ((groqErr as Error).name === 'AbortError') throw groqErr;
    throw new Error(
      isRetryableEdgeError(reason)
        ? 'Could not reach AI services. Check your internet connection and try again.'
        : (groqErr as Error).message || reason,
    );
  }
}

// Instead of guessing user intent with a regex (which misses everything the
// developer didn't anticipate), we ask the LLM itself what the user wants.
// One fast non-streaming call (~200ms) using GPT-OSS 20B on Groq.

export type CanvasAction =
  | 'edit_section'   // Rewrite one section with a specific instruction
  | 'add_section'    // Add a new section to the resume
  | 'move_section'   // Reorder sections
  | 'remove_section' // Delete a section from the canvas
  | 'organize_sections' // Sort sections into standard resume order
  | 'rewrite_all'    // Full resume re-optimization from scratch
  | 'cover_letter'   // Generate / refine cover letter
  | 'application_note' // Generate / refine application note
  | 'download'       // Download PDF / DOCX
  | 'apply'          // Apply to the job
  | 'chat';          // General conversation — no canvas action needed

export type CanvasSectionKey =
  | 'experience'
  | 'summary'
  | 'skills'
  | 'projects'
  | 'education'
  | 'certifications'
  | 'references'
  | 'awards'
  | 'languages'
  | 'personal'
  | null;

/** Canonical section keys → display titles used in the canvas. */
export const CANVAS_SECTION_TITLE_MAP: Record<string, string> = {
  experience: 'Work Experience',
  summary: 'Professional Summary',
  skills: 'Technical Skills',
  projects: 'Projects & Achievements',
  education: 'Education',
  certifications: 'Certifications',
  references: 'References',
  awards: 'Awards',
  research: 'Research',
  languages: 'Languages',
  personal: 'Personal Information',
};

export function canvasSectionDisplayTitle(key: string): string {
  const k = key.toLowerCase().trim();
  if (CANVAS_SECTION_TITLE_MAP[k]) return CANVAS_SECTION_TITLE_MAP[k];
  return key
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

function parseCanvasSectionKey(text: string): CanvasSectionKey {
  return parseCanvasSectionKeyFromText(text) as CanvasSectionKey;
}

/** Local copies — avoid circular import with canvasSectionUtils (which imports this module). */
function detectConsultationIntentLocal(text: string): boolean {
  const t = text.trim().toLowerCase();
  if (
    /\b(what (other|else)|which sections?|what sections? can|what can you add|what do you suggest|tell me what|list (the )?sections?|what sections? (are|do)|how many sections?)\b/i.test(t)
  ) {
    return true;
  }
  if (/\?/.test(t) && /\b(what|which|how|can you|could you|should i|do you)\b/i.test(t)) {
    if (/\b(section|add|include|resume|canvas)\b/i.test(t)) return true;
  }
  return false;
}

function wantsOrganizeSectionsLocal(text: string): boolean {
  return (
    /\b(organis[ez]|reorganis[ez]|sort|arrange|order properly|proper order|properly|canonical order|fix order)\b/i.test(text) &&
    /\b(section|resume|order|layout)\b/i.test(text)
  );
}

/** Regex fallback when the LLM classifier is unavailable or low-confidence. */
export function detectCanvasIntentHeuristic(userMessage: string): CanvasIntent | null {
  const t = userMessage.trim();
  if (!t) return null;

  if (wantsRemoveSection(t)) {
    const removeKey = parseRemoveSectionKeyFromText(t);
    if (removeKey) {
      return {
        action: 'remove_section',
        section: removeKey as CanvasSectionKey,
        instruction: t,
        confidence: 0.94,
      };
    }
  }

  if (detectConsultationIntentLocal(t)) {
    return { action: 'chat', section: null, instruction: t, confidence: 0.95 };
  }

  if (wantsOrganizeSectionsLocal(t)) {
    return { action: 'organize_sections', section: null, instruction: t, confidence: 0.95 };
  }

  const placement = parsePlacementFromUserText(t);
  const movedKey = placement?.moveKey ?? parseMovedSectionKeyFromText(t);
  const sectionKey = movedKey ?? parseCanvasSectionKeyFromText(t);
  if (sectionKey) {
    if (placement || extractPlacementInstruction(t) || movedKey) {
      return {
        action: 'move_section',
        section: (placement?.moveKey ?? movedKey ?? sectionKey) as CanvasSectionKey,
        instruction: t,
        confidence: 0.9,
      };
    }
    if (
      wantsDummyOrSampleFill(t) ||
      wantsPreserveAndExpandContent(t) ||
      /\b(enhance|improve|expand|fill\s+out|more\s+detai\w*|add\s+details?|add\s+values?|write\s+details?)\b/i.test(t)
    ) {
      return {
        action: 'edit_section',
        section: sectionKey as CanvasSectionKey,
        instruction: t,
        confidence: 0.88,
      };
    }
    if (/\b(add|insert|create|include|new)\b/i.test(t) && /\b(section|projects?|education|experience|skills?|certif)/i.test(t)) {
      return {
        action: 'add_section',
        section: sectionKey as CanvasSectionKey,
        instruction: t,
        confidence: 0.85,
      };
    }
  }

  if (
    /\b(move|reorder|relocate|shift|swap|place|position)\b/i.test(t) &&
    /\b(section|experience|education|skills|projects?|summary|certifications|before|after|below|under|first|top)\b/i.test(t)
  ) {
    return {
      action: 'move_section',
      section: parseCanvasSectionKey(t),
      instruction: t,
      confidence: 0.72,
    };
  }

  if (
    /\b(below|under|after|before|above)\b/i.test(t) &&
    parseCanvasSectionKey(t)
  ) {
    return {
      action: 'move_section',
      section: parseCanvasSectionKey(t),
      instruction: t,
      confidence: 0.78,
    };
  }

  if (/\b(add|create|include|insert|new)\b/i.test(t) && /\b(section|experience|education|skills|projects|certifications|awards|languages)\b/i.test(t)) {
    return {
      action: 'add_section',
      section: parseCanvasSectionKey(t),
      instruction: t,
      confidence: 0.72,
    };
  }

  if (/\b(edit|update|change|improve|rewrite|fix|refine)\b/i.test(t) && parseCanvasSectionKey(t)) {
    return {
      action: 'edit_section',
      section: parseCanvasSectionKey(t),
      instruction: t,
      confidence: 0.68,
    };
  }

  return null;
}

export interface CanvasIntent {
  action: CanvasAction;
  /** Which resume section to target (edit / add / move). */
  section: CanvasSectionKey;
  /** The exact instruction to pass to the optimizer. */
  instruction: string;
  /** How confident the classifier is (0-1). Below 0.5 → fallback to chat. */
  confidence: number;
}

/**
 * Classify user intent using the LLM when a canvas is open.
 * Returns structured JSON so the caller can take the right action
 * regardless of how the user phrased their request.
 *
 * Fast path: GPT-OSS 20B via ai-companion, non-streaming, ~150ms round-trip.
 */
export async function classifyCanvasIntent(
  userMessage: string,
  canvasContext: {
    jobTitle: string;
    jobCompany: string;
    activeSections: string[];
    activeTab: string;
    currentPhase: string;
  },
  signal?: AbortSignal,
): Promise<CanvasIntent> {
  const fallback: CanvasIntent = {
    action: 'chat', section: null, instruction: userMessage, confidence: 0,
  };

  const sectionsList = canvasContext.activeSections.length > 0
    ? canvasContext.activeSections.map((s, i) => `  ${i + 1}. ${s}`).join('\n')
    : '  (none yet)';

  const systemPrompt = `You are an intent classifier for a resume optimization assistant.
The user is currently looking at their optimized resume canvas.
Canvas state: job="${canvasContext.jobTitle} at ${canvasContext.jobCompany}", tab="${canvasContext.activeTab}", phase="${canvasContext.currentPhase}".

Current sections in the resume (in order):
${sectionsList}

Classify the user's message and reply with ONLY valid JSON — no explanation, no markdown:
{
  "action": one of ["edit_section","add_section","move_section","remove_section","organize_sections","rewrite_all","cover_letter","application_note","download","apply","chat"],
  "section": one of ["experience","summary","skills","projects","education","certifications","references","awards","languages","personal",null],
  "instruction": "the specific rewriting instruction to pass to the optimizer",
  "confidence": 0.0 to 1.0
}

Rules:
- "edit_section": user wants to change/improve/fix ONE existing section
- "add_section": user wants to ADD a NEW section not currently in the resume (e.g. "add certifications")
- "move_section": user wants to REORDER or MOVE a section (e.g. "move projects after experience", "add projects below work experience" when Projects already exists, "put education first")
- "remove_section": user wants to DELETE or REMOVE a section from the resume (e.g. "remove projects", "delete achievements section", "drop references") — do NOT rewrite other sections
- "organize_sections": user wants sections sorted into standard resume order — NOT a content rewrite (e.g. "organise all sections properly", "fix section order")
- "rewrite_all": user wants the ENTIRE resume rewritten again — NOT a single section (e.g. "redo all", "start fresh", "re optimize everything")
- "chat": general question or consultation — do NOT mutate canvas (e.g. "what other sections can I add?", "what should I do?", "tell me")
- "application_note": user wants an application note / pitch written
- "download": user wants to download or save the resume
- "apply": user wants to apply to the job
- "chat": general question, small talk, not a canvas action (e.g. "i dont know", "what should i do", "tell me", "what sections can you add")
- "cover_letter": user wants a cover letter written or changed
- instruction: REWRITE the user's intent as a crisp, professional optimizer directive (e.g. "Add 3 quantified achievement bullets using metrics like %, $, or time saved"). Fix typos. Make it specific. Reference which section by name.
- confidence: 1.0 if obvious, 0.5 if ambiguous, 0.1 if clearly just chat or unclear`;

  const parseIntentResponse = (raw: string): CanvasIntent | null => {
    const jsonMatch = raw.match(/\{[\s\S]*\}/);
    if (!jsonMatch) return null;
    const parsed = JSON.parse(jsonMatch[0]) as Partial<CanvasIntent>;
    return {
      action:      parsed.action      ?? 'chat',
      section:     parsed.section     ?? null,
      instruction: parsed.instruction ?? userMessage,
      confidence:  typeof parsed.confidence === 'number' ? parsed.confidence : 0.5,
    };
  };

  const callClassifier = async (): Promise<CanvasIntent | null> => {
    const raw = await invokeAiCompanionJson(systemPrompt, userMessage, { maxTokens: 180, temperature: 0 }, signal);
    if (!raw) return null;
    return parseIntentResponse(raw);
  };

  try {
    const intent = await callClassifier();
    if (!intent) return fallback;
    console.info('[intent]', userMessage, '→', intent);
    return intent;
  } catch {
    return fallback;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Tool-call based canvas classifier (replaces string-matching router)
// ─────────────────────────────────────────────────────────────────────────────

export type CanvasToolCall =
  | { tool: 'reorder_sections'; move_section: string; placement: 'before' | 'after' | 'first' | 'last'; anchor: string; chat_response: string }
  | { tool: 'patch_section';    section: string; instruction: string; chat_response: string }
  | { tool: 'remove_section';   section: string; chat_response: string }
  | { tool: 'rewrite_all';      chat_response: string }
  | { tool: 'chat_only';        response: string };

/**
 * Classify canvas intent using Groq tool-calling (strict structured output).
 *
 * The LLM must pick exactly ONE of three tools:
 *   • reorder_sections — move/sort sections, never rewrites content
 *   • patch_section    — edit / improve / add a single section's content
 *   • chat_only        — answer a question; never touches the canvas
 *
 * Because the action and the response text are returned in the SAME API call,
 * the chat message and the canvas mutation are always coupled — the agent can
 * never say "I updated X" without actually updating X.
 */
export async function classifyCanvasToolCall(
  userMessage: string,
  canvasCtx: {
    jobTitle:       string;
    jobCompany:     string;
    activeSections: string[];
    currentPhase:   string;
    /** Section titles → first 200 chars of content (for context). */
    sectionPreviews?: Record<string, string>;
  },
  signal?: AbortSignal,
  recentHistory: Array<{ role: string; content: string }> = [],
): Promise<CanvasToolCall> {
  const chatOnly: CanvasToolCall = { tool: 'chat_only', response: '' };

  const sectionsList = canvasCtx.activeSections.length > 0
    ? canvasCtx.activeSections.map((s, i) => `  ${i + 1}. ${s}`).join('\n')
    : '  (no sections on canvas yet)';

  const previews = canvasCtx.sectionPreviews ?? {};
  const previewBlock = Object.keys(previews).length > 0
    ? '\nSection content previews:\n' + Object.entries(previews).map(([t, c]) => `  [${t}]: ${c}`).join('\n')
    : '';

  const systemPrompt = `You are the resume canvas agent for GoGrabJob. Interpret the user's message naturally — fix typos, infer intent from context and recent chat.
Job: "${canvasCtx.jobTitle}" at "${canvasCtx.jobCompany}". Phase: ${canvasCtx.currentPhase}.

Sections on canvas (in order):
${sectionsList}${previewBlock}

Call exactly ONE tool:
- patch_section: edit, improve, add, bold/emphasize, or rewrite ONE section. Includes "yes" when you previously offered to edit a section.
- remove_section: delete a section entirely.
- reorder_sections: move or sort sections without changing text.
- rewrite_all: ONLY when user explicitly wants the ENTIRE resume rewritten from scratch (e.g. "re-optimize everything", "start fresh"). NEVER for "yes" to a single-section edit or bold formatting.
- chat_only: questions, advice, consultation — no canvas mutation.

Rules:
- User already named the action → execute it. Do NOT ask permission again in chat_only.
- "yes"/"ok"/"sure" → read recent chat: if you offered a section edit, use patch_section for that section; if you offered full re-optimize, use rewrite_all.
- Typos like "emphazie", "imprve", "certificatiosn" → infer correct intent.
- Bold/emphasis on skills/experience → patch_section with instruction to apply **bold** to category labels and key terms.

For patch_section.instruction: crisp directive for the content generator (fix typos, be specific).
For chat_only.response: full helpful reply.
For *_chat_response: one sentence confirming the action.

Reply with ONLY valid JSON matching exactly ONE tool call shape:
{"tool":"reorder_sections","move_section":"...","placement":"before|after|first|last","anchor":"...","chat_response":"..."}
{"tool":"patch_section","section":"...","instruction":"...","chat_response":"..."}
{"tool":"remove_section","section":"...","chat_response":"..."}
{"tool":"rewrite_all","chat_response":"..."}
{"tool":"chat_only","response":"..."}`;

  const historyBlock = recentHistory.slice(-6).map((h) =>
    `${h.role}: ${h.content.slice(0, 600)}`,
  ).join('\n');

  const userPayload = [
    historyBlock ? `Recent chat:\n${historyBlock}\n` : '',
    `User message: ${userMessage}`,
  ].filter(Boolean).join('\n');

  try {
    const raw = await invokeAiCompanionJson(systemPrompt, userPayload, { maxTokens: 500, temperature: 0 }, signal);
    if (!raw) return chatOnly;

    const jsonMatch = raw.match(/\{[\s\S]*\}/);
    if (!jsonMatch) return chatOnly;

    const args = JSON.parse(jsonMatch[0]) as Record<string, string>;
    const name = args.tool ?? '';
    console.info('[canvas-tool]', userMessage.slice(0, 60), '→', name, args);

    /** Map fuzzy section names → canonical keys (handles "work experience", "contact", etc.) */
    function normalizeSectionKey(raw: string): string {
      const s = (raw ?? '').toLowerCase().replace(/[^a-z_]/g, '');
      if (/work|experi/.test(s)) return 'experience';
      if (/summar|profile|object/.test(s)) return 'summary';
      if (/skill|tech|competenc/.test(s)) return 'skills';
      if (/project|portfolio/.test(s)) return 'projects';
      if (/educat|degree|academic/.test(s)) return 'education';
      if (/certif|licen/.test(s)) return 'certifications';
      if (/refer/.test(s)) return 'references';
      if (/award|honou?r|reward/.test(s)) return 'awards';
      if (/research|publication/.test(s)) return 'research';
      if (/lang/.test(s)) return 'languages';
      if (/personal|contact|info/.test(s)) return 'personal';
      if (/cover/.test(s)) return 'cover_letter';
      if (/application|note|pitch/.test(s)) return 'application_note';
      return s || 'summary';
    }

    function normalizePlacement(raw: string): 'before' | 'after' | 'first' | 'last' {
      const s = (raw ?? '').toLowerCase();
      if (s.includes('before') || s.includes('above')) return 'before';
      if (s.includes('after') || s.includes('below') || s.includes('under')) return 'after';
      if (s.includes('first') || s.includes('top')) return 'first';
      return 'last';
    }

    if (name === 'reorder_sections') {
      return {
        tool:          'reorder_sections',
        move_section:  normalizeSectionKey(args.move_section ?? ''),
        placement:     normalizePlacement(args.placement ?? ''),
        anchor:        normalizeSectionKey(args.anchor ?? ''),
        chat_response: args.chat_response ?? `Moving ${args.move_section} section.`,
      };
    }
    if (name === 'patch_section') {
      return {
        tool:          'patch_section',
        section:       normalizeSectionKey(args.section ?? 'summary'),
        instruction:   args.instruction   ?? userMessage,
        chat_response: args.chat_response ?? `Updating ${args.section} section.`,
      };
    }
    if (name === 'remove_section') {
      return {
        tool:          'remove_section',
        section:       normalizeSectionKey(args.section ?? ''),
        chat_response: args.chat_response ?? `Removed ${args.section} section.`,
      };
    }
    if (name === 'rewrite_all') {
      return {
        tool:          'rewrite_all',
        chat_response: args.chat_response ?? 'Re-optimizing your entire resume for this role.',
      };
    }
    if (name === 'chat_only') {
      return { tool: 'chat_only', response: args.response ?? '' };
    }
    return chatOnly;
  } catch {
    return chatOnly;
  }
}

/**
 * Call Groq OpenAI-compatible API directly from the browser using SSE streaming.
 * Uses VITE_GROQ_API_KEY — already exposed in .env.development.
 * On 429: retries 8b if on 70b, then throws for cloud fallback chain.
 */
async function runGroqDirectStream(
  groqModel: string,
  messages: Array<{ role: string; content: string }>,
  onDelta: (delta: string, accumulated: string) => void,
  signal?: AbortSignal,
): Promise<StreamCompletionResult> {
  const t0 = Date.now();

  const res = await fetch('https://api.groq.com/openai/v1/chat/completions', {
    method:  'POST',
    signal,
    headers: {
      'Content-Type':  'application/json',
      'Authorization': `Bearer ${GROQ_API_KEY}`,
    },
    body: JSON.stringify({ model: groqModel, messages, stream: true }),
  });

  if (!res.ok || !res.body) {
    const errText = await res.text().catch(() => '');
    const err = new Error(`Groq direct ${res.status}: ${errText.slice(0, 200)}`) as Error & { status?: number };
    err.status = res.status;
    throw err;
  }

  const reader  = res.body.getReader();
  const decoder = new TextDecoder();
  let accumulated = '';
  let buffer      = '';

  while (true) {
    if (signal?.aborted) throw new DOMException('aborted', 'AbortError');
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';
    for (const line of lines) {
      const trimmed = line.replace(/^data:\s*/, '').trim();
      if (!trimmed || trimmed === '[DONE]') continue;
      try {
        const parsed = JSON.parse(trimmed) as {
          choices?: Array<{ delta?: { content?: string } }>;
        };
        const chunk = parsed.choices?.[0]?.delta?.content ?? '';
        if (chunk) {
          accumulated += chunk;
          onDelta(chunk, stripActionTag(accumulated));
        }
      } catch { /* non-JSON SSE line */ }
    }
  }

  const thinkingMs = Date.now() - t0;
  const { thinking, clean } = extractThinkBlock(accumulated);

  const parsedAction = parseActionTag(clean);
  if (parsedAction) {
    return {
      content: stripActionTag(clean).trim(),
      ...parsedAction,
      thinkingMs,
      model: `groq/${groqModel}`,
      backend: 'groq',
      thinking: thinking || undefined,
    };
  }
  return {
    content: clean.trim() || accumulated.trim(),
    thinkingMs,
    model: `groq/${groqModel}`,
    backend: 'groq',
    thinking: thinking || undefined,
  };
}

async function streamCompletionGroqDirect(
  prompt: string,
  history: AgentHistoryItem[],
  onDelta: (delta: string, accumulated: string) => void,
  signal?: AbortSignal,
  opts: StreamCompletionOptions & { groqModel?: string } = {},
): Promise<StreamCompletionResult> {
  if (signal?.aborted) throw new DOMException('aborted', 'AbortError');
  if (!GROQ_API_KEY) throw new Error('VITE_GROQ_API_KEY is not set');

  const preferredModel = normalizeGroqModelId(opts.groqModel ?? resolveAgentGroqModel());
  const messages = buildAgentChatMessages(prompt, history, opts);

  const totalEstTokens = estimateTokens(messages.map((m) => m.content).join(''));
  const groqModel =
    totalEstTokens > 7_000 ? GROQ_MODEL_8B : preferredModel;

  if (groqModel !== preferredModel) {
    console.info(
      `[Groq] large prompt (~${totalEstTokens} tok) → auto-switched to ${groqModel} (higher TPM limit)`,
    );
  }

  const tryModels = [groqModel, GROQ_MODEL_8B, GROQ_MODEL_70B].filter(
    (m, i, arr) => arr.indexOf(m) === i,
  );

  let lastErr: unknown;
  for (const model of tryModels) {
    try {
      return await runGroqDirectStream(model, messages, onDelta, signal);
    } catch (err) {
      if ((err as Error).name === 'AbortError') throw err;
      lastErr = err;
      const status = (err as Error & { status?: number }).status ?? 0;
      const msg = (err as Error).message ?? '';
      if (isGroqModelNotFound(status, msg)) {
        console.warn(`[Groq] model ${model} unavailable — trying next fallback`);
        continue;
      }
      if (
        model !== GROQ_MODEL_8B &&
        isGroq70bModel(model) &&
        isGroqRateLimited(status, msg)
      ) {
        console.warn('[Groq] large model rate-limited, retrying with fast fallback');
        continue;
      }
      throw err;
    }
  }
  throw lastErr;
}

/**
 * Web-augmented Groq call: fetch search results first, inject as context,
 * then stream the AI response. Returns citations alongside the reply.
 */
export async function streamCompletionWithWebSearch(
  prompt: string,
  history: AgentHistoryItem[],
  onDelta: (delta: string, accumulated: string) => void,
  signal?: AbortSignal,
  opts: StreamCompletionOptions & { groqModel?: string } = {},
): Promise<StreamCompletionResult & { citations?: WebSearchResult[] }> {
  // 1. Run web search
  onDelta('', '');  // signal "loading" early
  const searchResults = await performWebSearch(prompt);

  // 2. Build context block from results
  const webCtx = searchResults.length > 0
    ? [
        'LIVE WEB SEARCH RESULTS (use these to answer — cite them):',
        ...searchResults.map((r, i) => `[${i+1}] ${r.title}\nURL: ${r.url}\n${r.snippet}`),
      ].join('\n\n')
    : '';

  const augmentedOpts: StreamCompletionOptions = {
    ...opts,
    systemPromptHint: [
      'You are answering with LIVE WEB SEARCH RESULTS provided in this prompt.',
      'Always cite sources using [1], [2], etc. format. Include the URLs at the end.',
      'Be concise and factual.',
      opts.systemPromptHint ?? '',
    ].filter(Boolean).join('\n'),
    userContext: [opts.userContext ?? '', webCtx].filter(Boolean).join('\n\n'),
  };

  console.info('[ai-agent] using Gemini + Web Search');
  try {
    const result = await streamCompletionGeminiEdge(prompt, history, onDelta, signal, augmentedOpts);
    return { ...result, citations: searchResults.length > 0 ? searchResults : undefined };
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err;
    console.warn('[ai-agent] Gemini web path failed — falling back to Groq');
    const groqResult = await streamCompletionGroqDirect(prompt, history, onDelta, signal, {
      ...augmentedOpts,
      groqModel: normalizeGroqModelId(opts.groqModel ?? GROQ_MODEL_70B),
    });
    return { ...groqResult, citations: searchResults.length > 0 ? searchResults : undefined };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Public: streamCompletion — cloud-first (Ollama optional via VITE_USE_OLLAMA)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * User-facing model label — hides provider/model IDs from non-admin users.
 */
export function formatPublicAgentModelLabel(modelId: string | null | undefined): string {
  const m = (modelId ?? '').trim();
  if (!m) return 'GoGrabJob AI';
  if (m.startsWith(WEB_MODEL_PREFIX)) return 'Research AI';
  if (m.startsWith(GEMINI_MODEL_PREFIX) || m.startsWith(CLOUD_MODEL_PREFIX)) {
    if (/lite|flash-lite/i.test(m)) return 'Fast AI';
    if (/pro|120b|70b|advanced/i.test(m)) return 'Advanced AI';
    return 'Gemini AI';
  }
  if (m.startsWith('groq/') || m.startsWith('groq-')) return 'GoGrabJob AI';
  return 'GoGrabJob AI';
}

export async function streamCompletion(
  prompt: string,
  history: AgentHistoryItem[],
  onDelta: (delta: string, accumulated: string) => void,
  signal?: AbortSignal,
  opts: StreamCompletionOptions = {},
): Promise<StreamCompletionResult & { citations?: WebSearchResult[] }> {
  const modelOpt = opts.model ?? '';

  // groq-web: web search + Gemini edge (Groq fallback inside)
  if (modelOpt.startsWith(WEB_MODEL_PREFIX)) {
    return streamCompletionWithWebSearch(prompt, history, onDelta, signal, opts);
  }

  // VITE_USE_OLLAMA=true → local first
  if (USE_OLLAMA) {
    const available = await isOllamaAvailable();
    if (available) {
      console.info('[ai-agent] VITE_USE_OLLAMA=true → Ollama at', OLLAMA_BASE_URL);
      return streamCompletionOllama(prompt, history, onDelta, signal, opts);
    }
    console.warn('[ai-agent] VITE_USE_OLLAMA=true but Ollama not reachable at', OLLAMA_BASE_URL, '— using Gemini edge');
  }

  // gemini-cloud: / groq-cloud: → Gemini via ai-companion edge (server key)
  if (isCloudGeminiModel(modelOpt) || modelOpt.startsWith(GEMINI_MODEL_PREFIX)) {
    try {
      return await streamCompletionGeminiEdge(prompt, history, onDelta, signal, opts);
    } catch (err) {
      if ((err as Error).name === 'AbortError') throw err;
      return streamCompletionCloudFallback(prompt, history, onDelta, signal, opts, err);
    }
  }

  // Ollama local model routing.
  //
  // A model string is treated as a local Ollama model if:
  //   (a) VITE_USE_OLLAMA=true — always try Ollama first for any non-cloud model
  //   (b) OR the selected model name looks like an Ollama tag (has a colon like
  //       "qwen2.5:latest", "llama3.2:3b", "gpt-oss:20b" etc.) and does NOT
  //       start with a cloud-platform prefix (groq-cloud:, groq-web:).
  //
  // Note: we intentionally do NOT exclude "gpt" prefixes here — users can pull
  // locally-named models like "gpt-oss:20b" via Ollama and they should route
  // to local. The cloud prefix guards above already handled groq-cloud:/ groq-web:.
  const looksLikeOllamaTag = modelOpt.includes(':') &&
    !isCloudGeminiModel(modelOpt) &&
    !modelOpt.startsWith(WEB_MODEL_PREFIX);

  if (USE_OLLAMA || looksLikeOllamaTag) {
    const available = await isOllamaAvailable();
    if (available) {
      console.info('[ai-agent] routing to Ollama at', OLLAMA_BASE_URL, '· requested model:', modelOpt || 'auto-detect');
      return streamCompletionOllama(prompt, history, onDelta, signal, opts);
    }
    console.warn('[ai-agent] Ollama not reachable — falling back to Gemini edge');
  }
  try {
    return await streamCompletionGeminiEdge(prompt, history, onDelta, signal, opts);
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err;
    return streamCompletionCloudFallback(prompt, history, onDelta, signal, opts, err);
  }
}

export type OllamaProbeResult =
  | { status: 'ok';           models: string[] }
  | { status: 'cors_blocked'; models: string[] } // service up, GET works, but POST CORS blocked
  | { status: 'offline';      models: [] };       // not running / unreachable

/**
 * Probe Ollama — returns both the list of models AND a status so the UI can
 * show a precise error instead of silently falling back.
 *
 * CORS detection strategy:
 *   GET /api/tags is cross-origin (localhost:5173 → localhost:11434), so the
 *   browser sends an Origin header and Ollama echoes back
 *   Access-Control-Allow-Origin IF OLLAMA_ORIGINS covers our origin.
 *   We read that header directly — no POST /api/chat needed.
 *
 *   Why not use POST /api/chat?
 *   Large models (e.g. gpt-oss:20b) take 10–30 s to load into GPU memory.
 *   Any short timeout on that POST incorrectly looked like a CORS failure.
 */
export async function probeOllama(): Promise<OllamaProbeResult> {
  // ── Phase 1: reachability + model list (GET /api/tags) ───────────────────
  // Simple GET works even without CORS — used only to check server is up and
  // get the list of pulled models.
  let models: string[] = [];
  try {
    const ctrl  = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 3_000);
    const res   = await fetch(`${OLLAMA_BASE_URL}/api/tags`, { signal: ctrl.signal });
    clearTimeout(timer);
    if (!res.ok) return { status: 'offline', models: [] };
    const data = (await res.json()) as { models?: { name: string }[] };
    models = (data.models ?? []).map((m) => m.name);
  } catch {
    return { status: 'offline', models: [] };
  }

  // ── Phase 2: real CORS check via POST /api/chat ───────────────────────────
  // We CANNOT simulate a CORS preflight manually — browsers silently strip
  // the Access-Control-Request-Method / Access-Control-Request-Headers
  // headers from manual fetch() calls (they are "forbidden headers"), so
  // Ollama receives a plain OPTIONS and responds without ACAO headers.
  //
  // The only reliable test is to attempt an actual POST with Content-Type:
  // application/json — this triggers the browser's REAL automatic preflight.
  //   • CORS blocked → fetch() throws TypeError (browser killed the preflight)
  //   • CORS ok      → any HTTP response is returned (even 404 / model error)
  //
  // We use num_predict:0 to avoid generating tokens. The only delay is
  // model load time (if the model isn't already in GPU memory).
  // A 10 s timeout is enough for an already-warm model; a cold 20 B model
  // may take longer but we'll still correctly detect CORS ok/blocked.
  const testModel = models[0] ?? OLLAMA_MODEL;
  try {
    const ctrl2  = new AbortController();
    const timer2 = setTimeout(() => ctrl2.abort(), 10_000);
    await fetch(`${OLLAMA_BASE_URL}/api/chat`, {
      method:  'POST',
      signal:  ctrl2.signal,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model:    testModel,
        messages: [{ role: 'user', content: 'hi' }],
        stream:   false,
        options:  { num_predict: 0 },
      }),
    });
    clearTimeout(timer2);
    // Any HTTP response (200, 404, 500…) means CORS passed — the browser
    // let the request through. Only a thrown TypeError means CORS blocked.
    return { status: 'ok', models };
  } catch (err) {
    const isAbort = (err as Error)?.name === 'AbortError';
    if (isAbort) {
      // Timed out — model is probably still loading into GPU memory.
      // Treat as CORS OK (if CORS were blocked we'd have gotten TypeError
      // almost immediately, not a timeout). Mark ok so the user can proceed.
      return { status: 'ok', models };
    }
    // TypeError = browser blocked the preflight → CORS not configured
    return { status: 'cors_blocked', models };
  }
}

/**
 * Fetch the list of models available in the local Ollama instance.
 * Returns an empty array if Ollama is not reachable or CORS is blocked.
 * @deprecated Use probeOllama() for new code — gives more detail.
 */
export async function listOllamaModels(): Promise<string[]> {
  const result = await probeOllama();
  return result.models;
}

/**
 * Perform a web search using DuckDuckGo Instant Answers API.
 * Returns an array of result objects or an empty array on failure.
 */
export interface WebSearchResult {
  title: string;
  url: string;
  snippet: string;
}

/**
 * performWebSearch — powered by Exa AI via the exa-search edge function.
 *
 * Exa provides semantic (neural) search with real page content snippets —
 * far more reliable than the previous DuckDuckGo/CORS-proxy approach.
 * Falls back to empty array if the edge function is unavailable.
 */
export async function performWebSearch(query: string): Promise<WebSearchResult[]> {
  try {
    const { data, error } = await supabase.functions.invoke<{
      ok:      boolean;
      results: Array<{ id: string; title: string; url: string; snippet: string; publishedDate: string | null; score: number }>;
      query:   string;
      error?:  string;
    }>('exa-search', {
      body: {
        query,
        type:       'neural',
        numResults: 8,
        useContents: true,
      },
    });

    if (error || !data?.ok) {
      console.warn('[performWebSearch] exa-search error:', error?.message ?? data?.error);
      return [];
    }

    return (data.results ?? []).map(r => ({
      title:   r.title,
      url:     r.url,
      snippet: r.snippet,
    }));
  } catch (e) {
    console.warn('[performWebSearch] unexpected error:', (e as Error).message);
    return [];
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// 🔬 DIAGNOSTIC: LLM connectivity + quality test
// ─────────────────────────────────────────────────────────────────────────────
//
// Run in browser console:
//   import('/src/modules/ai-agent/agentSession.ts').then(m => m.runLLMDiagnostic())
// OR the global shortcut (registered below):
//   window.__ggjDiag()
//
// Tests BOTH backends with an ATS-sections prompt and logs full results.

const ATS_TEST_PROMPT = `You are a professional resume expert.
Question: What are the standard sections in an ATS-compliant resume, and what additional sections can a candidate add to stand out?
Please list: (1) mandatory sections, (2) optional but impactful sections, (3) sections to avoid.
Be concise and practical.`;

export async function runLLMDiagnostic(): Promise<void> {
  const noop = () => { /* streaming tokens discarded in diagnostic */ };

  console.group('%c🔬 GoGrabJob LLM Diagnostic', 'color:#6366f1;font-size:14px;font-weight:bold');
  console.log('%cPrompt sent to both backends:', 'color:#888', ATS_TEST_PROMPT);
  console.groupEnd();

  // ── Test A: Gemini edge function ───────────────────────────────────────────
  console.group('%c[A] Gemini Edge Function (cloud)', 'color:#f59e0b;font-size:13px;font-weight:bold');
  console.time('gemini-latency');
  try {
    const geminiResult = await streamCompletionGeminiEdge(
      ATS_TEST_PROMPT,
      [],
      noop,
      undefined,
      {},
    );
    console.timeEnd('gemini-latency');
    console.log('%c✅ Gemini response:', 'color:#16a34a;font-weight:bold', geminiResult.content);
    console.log('%cMetadata:', 'color:#888', {
      backend: geminiResult.backend,
      model: geminiResult.model,
      thinkingMs: geminiResult.thinkingMs,
      action: geminiResult.action ?? '(none)',
    });
  } catch (err) {
    console.timeEnd('gemini-latency');
    console.error('%c❌ Gemini FAILED:', 'color:#dc2626;font-weight:bold', (err as Error).message);
  }
  console.groupEnd();

  // ── Test A2: Groq direct fallback ─────────────────────────────────────────
  console.group('%c[A2] Groq Direct (fallback)', 'color:#f59e0b;font-size:13px;font-weight:bold');
  console.time('groq-latency');
  try {
    const groqResult = await streamCompletionGroq(
      ATS_TEST_PROMPT,
      [],
      noop,
      undefined,
      {},
    );
    console.timeEnd('groq-latency');
    console.log('%c✅ Groq response:', 'color:#16a34a;font-weight:bold', groqResult.content);
    console.log('%cMetadata:', 'color:#888', {
      backend: groqResult.backend,
      model: groqResult.model,
      thinkingMs: groqResult.thinkingMs,
      action: groqResult.action ?? '(none)',
    });
  } catch (err) {
    console.timeEnd('groq-latency');
    console.error('%c❌ Groq FAILED:', 'color:#dc2626;font-weight:bold', (err as Error).message);
  }
  console.groupEnd();

  // ── Test B: Local Ollama ────────────────────────────────────────────────────
  console.group('%c[B] Local Ollama (llm)', 'color:#10b981;font-size:13px;font-weight:bold');
  const ollamaAvailable = await isOllamaAvailable();
  if (!ollamaAvailable) {
    console.warn('%c⚠️  Ollama not reachable at', 'color:#d97706', OLLAMA_BASE_URL,
      '— is it running? Start with: ollama serve');
    console.log('%cTo enable: set VITE_USE_OLLAMA=true and VITE_OLLAMA_URL in .env.development', 'color:#888');
    console.groupEnd();
    return;
  }
  console.time('ollama-latency');
  try {
    const ollamaResult = await streamCompletionOllama(
      ATS_TEST_PROMPT,
      [],
      noop,
      undefined,
      {},
    );
    console.timeEnd('ollama-latency');
    console.log('%c✅ Ollama response:', 'color:#16a34a;font-weight:bold', ollamaResult.content);
    console.log('%cMetadata:', 'color:#888', {
      backend: ollamaResult.backend,
      model: ollamaResult.model,
      thinkingMs: ollamaResult.thinkingMs,
      action: ollamaResult.action ?? '(none)',
    });
  } catch (err) {
    console.timeEnd('ollama-latency');
    console.error('%c❌ Ollama FAILED:', 'color:#dc2626;font-weight:bold', (err as Error).message);
  }
  console.groupEnd();

  console.log('%c🔬 Diagnostic complete — check groups above for full output', 'color:#6366f1;font-weight:bold');
}

// Register as a convenient global shortcut for browser console access
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__ggjDiag = runLLMDiagnostic;
  console.info(
    '%c[GoGrabJob] LLM diagnostic available → run window.__ggjDiag() in console',
    'color:#6366f1;font-size:11px',
  );
}
